import React from "react";
import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
import { useNavigate } from "react-router-dom";
import moment from 'moment';
import RectangleIcon from "@mui/icons-material/Rectangle";

// Define types for owner, approver, and application details
interface NotebookParticipantInfo {
    id: string;
    name: string;
    googleProfilePicture?: string;
}

interface NotebookApplicationInfo {
    id: string;
    applicationName: string;
}

// Interface for Notebook details (specifically the nested 'notebooks' part)
interface NotebookSpecificDetails {
    region?: string;
    zone?: string;
    workbenchType?: string;
    jupyterLabVersion?: string;
    machineType?: string;
    network?: string;
    subNetwork?: string;
}

// Main Notebook interface for the table
interface Notebook {
    id: string;
    name: string;
    application?: NotebookApplicationInfo;
    cloudProvider?: string;
    costLabelPairs?: { cost: number; label: string }[];
    status: 'pending' | 'active' | 'rejected' | 'approved' | 'provisioning' | 'failed' | string;
    details?: {
        notebooks?: NotebookSpecificDetails;
    };
    reasonForRejection?: string;
    createdAt?: string;
    updatedAt?: string;
    solutionOwner?: NotebookParticipantInfo | null;
    solutionApprover?: NotebookParticipantInfo | null;
}

interface NotebooksTableProps {
    notebooks: Notebook[];
    columnFilters: Record<string, boolean>;
    statusType?: 'pending' | 'active' | 'rejected' | 'approved' | 'provisioning' | 'failed' | string;
}

const NotebooksTable: React.FC<NotebooksTableProps> = ({
    notebooks,
    columnFilters,
    statusType,
}) => {
    const navigate = useNavigate();

    const handleRowClick = (notebookId: string) => {
        navigate(`/aisolutions/notebooks/details/${notebookId}`);
    };

    const dateFormatter = (dateValue: string | number | null | undefined) => {
        if (!dateValue) return "N/A";
        const numericDate = Number(dateValue);
        if (isNaN(numericDate) || numericDate === 0) {
          const isoDate = moment(dateValue, moment.ISO_8601, true);
          if (isoDate.isValid()) {
            return isoDate.format("DD/MM/YYYY | hh:mm A");
          }
          return "N/A";
        }
        return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
    };


    const headers = [
        { key: "name", label: "NOTEBOOK NAME", xs: 2.5 },
        { key: "cloudProvider", label: "CLOUD PROVIDER", xs: 1.25 },
        { key: "application", label: "APPLICATION", xs: 1.5 },
        { key: "region", label: "REGION", xs: 1.25 },
        { key: "machineType", label: "MACHINE TYPE", xs: 1.25 },
        { key: "workbenchType", label: "WORKBENCH TYPE", xs: 1.25 },
        { key: "solutionOwner", label: "OWNER", xs: 1.25 },
        { key: "createdAt", label: "ADDED ON", xs: 1.5 },
    ];

    let iconColor = "#FDB022";
    let listItemBgColor = "#FDB02210";

    if (statusType === "active" || statusType === "approved" || statusType === "success") {
        iconColor = "#12B76A";
        listItemBgColor = "#FFFFFF";
    } else if (statusType === "rejected" || statusType === "failed") {
        iconColor = "#F04438";
        listItemBgColor = "#F0443810";
    } else if (statusType === "pending" || statusType === "provisioning" || statusType === "submitted") {
        iconColor = "#FDB022";
        listItemBgColor = "#FDB02210";
    }


    return (
        <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
            <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
                <Box width="100%">
                    <Grid container direction="row" alignItems="center">
                        {headers.map(header => (
                            columnFilters[header.key] && (
                                <Grid item xs={header.xs as any} key={header.key}>
                                    <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
                                        {header.label}
                                    </Typography>
                                </Grid>
                            )
                        ))}
                    </Grid>
                </Box>
            </ListItem>

            {notebooks.map((notebook) => (
                <ListItem
                    key={notebook.id}
                    sx={{
                        mt: 1,
                        borderRadius: "8px",
                        cursor: "pointer",
                        px: 2,
                        width: '100%',
                        backgroundColor: listItemBgColor,
                        '&:hover': {
                            borderColor: "black",
                        }
                    }}
                    onClick={() => handleRowClick(notebook.id)}
                >
                    <Box width="100%">
                        <Grid container direction="row" alignItems="center">
                            {columnFilters.name && (
                                <Grid item xs={headers.find(h => h.key === 'name')?.xs as any}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <RectangleIcon
                                            sx={{
                                                color: iconColor,
                                                bgcolor: iconColor,
                                                width: "4px",
                                                height: "22px",
                                                marginRight: "8px",
                                            }}
                                        />
                                        <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
                                            {notebook.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.cloudProvider && (
                                <Grid item xs={headers.find(h => h.key === 'cloudProvider')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {notebook.cloudProvider?.toUpperCase() || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.application && (
                                <Grid item xs={headers.find(h => h.key === 'application')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {notebook.application?.applicationName || "-"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.region && (
                                <Grid item xs={headers.find(h => h.key === 'region')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {notebook.details?.notebooks?.region || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.machineType && (
                                <Grid item xs={headers.find(h => h.key === 'machineType')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {notebook.details?.notebooks?.machineType || "-"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.workbenchType && (
                                <Grid item xs={headers.find(h => h.key === 'workbenchType')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                        {notebook.details?.notebooks?.workbenchType || "N/A"}
                                    </Typography>
                                </Grid>
                            )}
                            {columnFilters.solutionOwner && (
                                <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
                                    <Box sx={{ display: "flex", alignItems: "center" }}>
                                        <Avatar
                                                alt={notebook.solutionOwner?.name || ""}
                                                src={notebook.solutionOwner?.googleProfilePicture} // <-- ADD THIS LINE
                                                sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: 'rgba(0,0,0,0.1)' }}
                                            >
                                                {/* This initial is now a fallback */}
                                                {notebook.solutionOwner && notebook.solutionOwner.name ? notebook.solutionOwner.name.charAt(0).toUpperCase() : null}
                                            </Avatar>
                                        <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
                                            {notebook.solutionOwner?.name || "N/A"}
                                        </Typography>
                                    </Box>
                                </Grid>
                            )}
                            {columnFilters.createdAt && (
                                <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
                                    <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
                                        {dateFormatter(notebook.createdAt)}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </ListItem>
            ))}
        </List>
    );
};

export default NotebooksTable;


// import React from "react";
// import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
// import { useNavigate } from "react-router-dom";
// import moment from 'moment';
// import RectangleIcon from "@mui/icons-material/Rectangle";

// // Define types for owner, approver, and application details
// interface NotebookParticipantInfo {
//     id: string;
//     name: string;
// }

// interface NotebookApplicationInfo {
//     id: string;
//     applicationName: string;
// }

// // Interface for Notebook details (specifically the nested 'notebooks' part)
// interface NotebookSpecificDetails {
//     region?: string;
//     zone?: string;
//     workbenchType?: string;
//     jupyterLabVersion?: string;
//     machineType?: string;
//     network?: string;
//     subNetwork?: string;
// }

// // Main Notebook interface for the table
// interface Notebook {
//     id: string;
//     name: string;
//     application?: NotebookApplicationInfo;
//     solutionType?: string;
//     costLabelPairs?: { cost: number; label: string }[];
//     status: 'pending' | 'active' | 'rejected' | 'approved' | 'provisioning' | 'failed' | string;
//     details?: {
//         notebooks?: NotebookSpecificDetails;
//     };
//     reasonForRejection?: string;
//     createdAt?: string;
//     updatedAt?: string;
//     solutionOwner?: NotebookParticipantInfo | null;
//     solutionApprover?: NotebookParticipantInfo | null;
// }

// interface NotebooksTableProps {
//     notebooks: Notebook[];
//     columnFilters: Record<string, boolean>;
//     statusType?: 'pending' | 'active' | 'rejected' | 'approved' | 'provisioning' | 'failed' | string;
// }

// const NotebooksTable: React.FC<NotebooksTableProps> = ({
//     notebooks,
//     columnFilters,
//     statusType,
// }) => {
//     const navigate = useNavigate();

//     const handleRowClick = (notebookId: string) => {
//         navigate(`/aisolutions/notebooks/details/${notebookId}`);
//     };

//     const dateFormatter = (dateValue: string | number | null | undefined) => {
//         if (!dateValue) return "N/A";
//         const numericDate = Number(dateValue);
//         if (isNaN(numericDate) || numericDate === 0) {
//           const isoDate = moment(dateValue, moment.ISO_8601, true);
//           if (isoDate.isValid()) {
//             return isoDate.format("DD/MM/YYYY | hh:mm A");
//           }
//           return "N/A";
//         }
//         return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
//     };


//     const headers = [
//         { key: "name", label: "NOTEBOOK NAME", xs: 2.5 },
//         { key: "application", label: "APPLICATION", xs: 1.5 },
//         { key: "solutionType", label: "SOLUTION TYPE", xs: 1.25 },
//         { key: "region", label: "REGION", xs: 1.25 },
//         { key: "machineType", label: "MACHINE TYPE", xs: 1.25 },
//         { key: "workbenchType", label: "WORKBENCH TYPE", xs: 1.25 },
//         { key: "solutionOwner", label: "OWNER", xs: 1.5 },
//         { key: "createdAt", label: "ADDED ON", xs: 1.5 },
//     ];

//     let iconColor = "#FDB022";
//     let listItemBgColor = "#FDB02210";

//     if (statusType === "active" || statusType === "approved" || statusType === "success") {
//         iconColor = "#12B76A";
//         listItemBgColor = "#FFFFFF";
//     } else if (statusType === "rejected" || statusType === "failed") {
//         iconColor = "#F04438";
//         listItemBgColor = "#F0443810";
//     } else if (statusType === "pending" || statusType === "provisioning" || statusType === "submitted") {
//         iconColor = "#FDB022";
//         listItemBgColor = "#FDB02210";
//     }


//     return (
//         <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
//             <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
//                 <Box width="100%">
//                     <Grid container direction="row" alignItems="center">
//                         {headers.map(header => (
//                             columnFilters[header.key] && (
//                                 <Grid item xs={header.xs as any} key={header.key}>
//                                     <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
//                                         {header.label}
//                                     </Typography>
//                                 </Grid>
//                             )
//                         ))}
//                     </Grid>
//                 </Box>
//             </ListItem>

//             {notebooks.map((notebook) => (
//                 <ListItem
//                     key={notebook.id}
//                     sx={{
//                         mt: 1,
//                         borderRadius: "8px",
//                         cursor: "pointer",
//                         px: 2,
//                         width: '100%',
//                         backgroundColor: listItemBgColor,
//                         '&:hover': {
//                             borderColor: "black",
//                         }
//                     }}
//                     onClick={() => handleRowClick(notebook.id)}
//                 >
//                     <Box width="100%">
//                         <Grid container direction="row" alignItems="center">
//                             {columnFilters.name && (
//                                 <Grid item xs={headers.find(h => h.key === 'name')?.xs as any}>
//                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                                         <RectangleIcon
//                                             sx={{
//                                                 color: iconColor,
//                                                 bgcolor: iconColor,
//                                                 width: "4px",
//                                                 height: "22px",
//                                                 marginRight: "8px",
//                                             }}
//                                         />
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
//                                             {notebook.name || "N/A"}
//                                         </Typography>
//                                     </Box>
//                                 </Grid>
//                             )}
//                             {columnFilters.application && (
//                                 <Grid item xs={headers.find(h => h.key === 'application')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.application?.applicationName || "-"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.solutionType && (
//                                 <Grid item xs={headers.find(h => h.key === 'solutionType')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.solutionType || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.region && (
//                                 <Grid item xs={headers.find(h => h.key === 'region')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.details?.notebooks?.region || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.machineType && (
//                                 <Grid item xs={headers.find(h => h.key === 'machineType')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.details?.notebooks?.machineType || "-"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.workbenchType && (
//                                 <Grid item xs={headers.find(h => h.key === 'workbenchType')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.details?.notebooks?.workbenchType || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.solutionOwner && (
//                                 <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         <Avatar
//                                             alt={notebook.solutionOwner?.name || ""}
//                                             sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: 'rgba(0,0,0,0.1)' }}
//                                         >
//                                             {notebook.solutionOwner && notebook.solutionOwner.name ? notebook.solutionOwner.name.charAt(0).toUpperCase() : null}
//                                         </Avatar>
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                             {notebook.solutionOwner?.name || "N/A"}
//                                         </Typography>
//                                     </Box>
//                                 </Grid>
//                             )}
//                             {columnFilters.createdAt && (
//                                 <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
//                                         {dateFormatter(notebook.createdAt)}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                         </Grid>
//                     </Box>
//                 </ListItem>
//             ))}
//         </List>
//     );
// };

// export default NotebooksTable;



// import React from "react";
// import { Box, Typography, Avatar, Grid, ListItem, List } from "@mui/material";
// import { useNavigate } from "react-router-dom";
// import moment from 'moment';
// import RectangleIcon from "@mui/icons-material/Rectangle";

// // Define types for owner and approver details
// interface NotebookParticipantInfo {
//     id: string;
//     name: string;
//     // Add other fields like email or googleProfilePicture if available and needed
// }

// // Interface for Notebook details (specifically the nested 'notebooks' part)
// interface NotebookSpecificDetails {
//     region?: string;
//     zone?: string;
//     workbenchType?: string;
//     jupyterLabVersion?: string;
//     machineType?: string;
//     network?: string;
//     subNetwork?: string;
// }

// // Main Notebook interface for the table
// interface Notebook {
//     id: string;
//     name: string;
//     solutionType?: string;
//     costLabelPairs?: { cost: number; label: string }[]; // Not directly used in table columns here
//     status: 'pending' | 'active' | 'rejected' | 'approved' | 'provisioning' | 'failed' | string; // Adjusted for typical notebook statuses
//     details?: {
//         notebooks?: NotebookSpecificDetails; // Matches the query structure
//     };
//     reasonForRejection?: string; // Not directly used in table columns here
//     createdAt?: string;
//     updatedAt?: string; // Not directly used in table columns here
//     solutionOwner?: NotebookParticipantInfo | null;
//     solutionApprover?: NotebookParticipantInfo | null; // Not directly used in table columns here
// }

// interface NotebooksTableProps {
//     notebooks: Notebook[];
//     columnFilters: Record<string, boolean>;
//     statusType?: 'pending' | 'active' | 'rejected' | 'approved' | 'provisioning' | 'failed' | string; // Prop to determine row styling
// }

// const NotebooksTable: React.FC<NotebooksTableProps> = ({
//     notebooks,
//     columnFilters,
//     statusType,
// }) => {
//     const navigate = useNavigate();

//     const handleRowClick = (notebookId: string) => {
//         navigate(`/aisolutions/notebooks/details/${notebookId}`); // Adjusted path
//     };

//     const dateFormatter = (dateValue: string | number | null | undefined) => {
//         if (!dateValue) return "N/A"; // Handle null or undefined dates gracefully
//         const numericDate = Number(dateValue);
//         // Check if it's a valid number (Unix timestamp)
//         if (isNaN(numericDate) || numericDate === 0) { // Also checking for 0 if it's an invalid default
//           // Attempt to parse if it's an ISO string
//           const isoDate = moment(dateValue, moment.ISO_8601, true);
//           if (isoDate.isValid()) {
//             return isoDate.format("DD/MM/YYYY | hh:mm A");
//           }
//           return "N/A"; // Return N/A if not a valid timestamp or ISO string
//         }
//         return moment(new Date(numericDate).toISOString()).format("DD/MM/YYYY | hh:mm A");
//     };


//     const headers = [
//         { key: "name", label: "NOTEBOOK NAME", xs: 2.5 },
//         { key: "solutionType", label: "SOLUTION TYPE", xs: 1.5 },
//         { key: "region", label: "REGION", xs: 1.5 },
//         { key: "machineType", label: "MACHINE TYPE", xs: 1.75 },
//         { key: "workbenchType", label: "WORKBENCH TYPE", xs: 1.75 },
//         { key: "solutionOwner", label: "OWNER", xs: 1.5 },
//         { key: "createdAt", label: "ADDED ON", xs: 1.5 },
//     ];

//     // Determine colors based on statusType
//     // Adjusted to include more common notebook statuses, assuming 'approved', 'provisioning' are like 'active' or 'pending'
//     let iconColor = "#FDB022"; // Default to pending color
//     let listItemBgColor = "#FDB02210"; // Default to pending background

//     if (statusType === "active" || statusType === "approved" || statusType === "success") {
//         iconColor = "#12B76A";
//         listItemBgColor = "#FFFFFF"; // Or "#12B76A10" if active also needs a colored background
//     } else if (statusType === "rejected" || statusType === "failed") {
//         iconColor = "#F04438";
//         listItemBgColor = "#F0443810";
//     } else if (statusType === "pending" || statusType === "provisioning" || statusType === "submitted") {
//         iconColor = "#FDB022";
//         listItemBgColor = "#FDB02210";
//     }


//     return (
//         <List sx={{ bgcolor: "transparent", px: 0, mx: 0, width: '100%' }}>
//             <ListItem sx={{ bgcolor: "#f2f2f2", borderRadius: "4px", mb: 0.5, px: 2, py: 1, width: '100%' }}>
//                 <Box width="100%">
//                     <Grid container direction="row" alignItems="center">
//                         {headers.map(header => (
//                             columnFilters[header.key] && (
//                                 <Grid item xs={header.xs as any} key={header.key}>
//                                     <Typography fontSize="12px" color="#000000" fontFamily="Gilroy-Medium">
//                                         {header.label}
//                                     </Typography>
//                                 </Grid>
//                             )
//                         ))}
//                     </Grid>
//                 </Box>
//             </ListItem>

//             {notebooks.map((notebook) => (
//                 <ListItem
//                     key={notebook.id}
//                     sx={{
//                         mt: 1,
//                         borderRadius: "8px",
//                         cursor: "pointer",
//                         px: 2,
//                         width: '100%',
//                         backgroundColor: listItemBgColor,
//                         '&:hover': {
//                             borderColor: "black", // Maintained from original
//                         }
//                     }}
//                     onClick={() => handleRowClick(notebook.id)}
//                 >
//                     <Box width="100%">
//                         <Grid container direction="row" alignItems="center">
//                             {columnFilters.name && (
//                                 <Grid item xs={headers.find(h => h.key === 'name')?.xs as any}>
//                                     <Box sx={{ display: 'flex', alignItems: 'center' }}>
//                                         <RectangleIcon
//                                             sx={{
//                                                 color: iconColor,
//                                                 bgcolor: iconColor,
//                                                 width: "4px",
//                                                 height: "22px",
//                                                 marginRight: "8px",
//                                             }}
//                                         />
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Medium" color="text.primary" noWrap>
//                                             {notebook.name || "N/A"}
//                                         </Typography>
//                                     </Box>
//                                 </Grid>
//                             )}
//                             {columnFilters.solutionType && (
//                                 <Grid item xs={headers.find(h => h.key === 'solutionType')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.solutionType || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.region && (
//                                 <Grid item xs={headers.find(h => h.key === 'region')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.details?.notebooks?.region || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.machineType && (
//                                 <Grid item xs={headers.find(h => h.key === 'machineType')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.details?.notebooks?.machineType || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.workbenchType && (
//                                 <Grid item xs={headers.find(h => h.key === 'workbenchType')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                         {notebook.details?.notebooks?.workbenchType || "N/A"}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                             {columnFilters.solutionOwner && (
//                                 <Grid item xs={headers.find(h => h.key === 'solutionOwner')?.xs as any}>
//                                     <Box sx={{ display: "flex", alignItems: "center" }}>
//                                         {/* Assuming solutionOwner doesn't have googleProfilePicture in the new query, adapt if it does */}
//                                         <Avatar
//                                             // src={notebook.solutionOwner?.googleProfilePicture || undefined} 
//                                             alt={notebook.solutionOwner?.name || ""}
//                                             sx={{ marginRight: "8px", height: 24, width: 24, fontSize: '0.75rem', bgcolor: 'rgba(0,0,0,0.1)' }}
//                                         >
//                                             {notebook.solutionOwner && notebook.solutionOwner.name ? notebook.solutionOwner.name.charAt(0).toUpperCase() : null}
//                                         </Avatar>
//                                         <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333" noWrap>
//                                             {notebook.solutionOwner?.name || "N/A"}
//                                         </Typography>
//                                     </Box>
//                                 </Grid>
//                             )}
//                             {columnFilters.createdAt && (
//                                 <Grid item xs={headers.find(h => h.key === 'createdAt')?.xs as any}>
//                                     <Typography fontSize="14px" fontFamily="Gilroy-Regular" color="#333333">
//                                         {dateFormatter(notebook.createdAt)}
//                                     </Typography>
//                                 </Grid>
//                             )}
//                         </Grid>
//                     </Box>
//                 </ListItem>
//             ))}
//         </List>
//     );
// };

// export default NotebooksTable;